import { useTranslation } from "hhn-commons";
import CommonDialog from "./CommonDialog/CommonDialog";
import { useState } from "react";
import {
  ConfirmationCenteredButton,
  ConfirmationMessageWrapper,
} from "./ConfirmationDialog.style";

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  confirmationTitle: string;
  confirmationMessage: string;
  confirmationButtonText: string;
  confirmationCancelText?: string;
  onConfirmationAction: () => void;
  onCancelAction?: () => void;
  shouldCloseOnConfirm?: boolean;
  disableConfirm?: boolean;
  children?: React.ReactNode;
}

export const ConfirmationDialog = ({
  isOpen,
  onClose,
  confirmationTitle,
  confirmationMessage,
  confirmationButtonText,
  confirmationCancelText,
  onConfirmationAction,
  onCancelAction,
  shouldCloseOnConfirm = true,
  disableConfirm,
  children,
}: ConfirmationDialogProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const cancelText = confirmationCancelText || tNs("cancel");
  const onCancel = onCancelAction || onClose;
  const onConfirm = async () => {
    setIsLoading(true);
    await onConfirmationAction();
    setIsLoading(false);
    shouldCloseOnConfirm && onClose();
  };

  return (
    <CommonDialog
      data-testid="common-dialog"
      isDialogOpen={isOpen}
      onDialogClose={onClose}
      title={confirmationTitle}
      titleAlignment="center"
      maxWidth="xs"
      actionContent={
        <>
          <ConfirmationCenteredButton
            variant="outlined"
            data-testid="cancel-confirmation-dialog"
            onClick={onCancel}
            style={{ marginRight: "8px" }}
          >
            {cancelText}
          </ConfirmationCenteredButton>
          <ConfirmationCenteredButton
            variant="contained"
            loading={isLoading}
            loadingPosition="center"
            data-testid="confirm-confirmation-dialog"
            onClick={onConfirm}
            disabled={disableConfirm}
          >
            {confirmationButtonText}
          </ConfirmationCenteredButton>
        </>
      }
    >
      <ConfirmationMessageWrapper data-testid="confirmation-dialog-message">
        {confirmationMessage}
      </ConfirmationMessageWrapper>
      {children}
    </CommonDialog>
  );
};
